import { MutationTuple, useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import {
  useQueryParam,
  StringParam,
  NumberParam,
  ArrayParam,
  withDefault
} from "use-query-params";

import { Container, makeStyles } from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import PageTitle from "src/components/PageTitle";
import { useSubaccount } from "src/components/SubaccountProvider";
import {
  archiveCampaign,
  archiveCampaignVariables
} from "src/graphql/generated/archiveCampaign";
import {
  copyCampaign,
  copyCampaignVariables
} from "src/graphql/generated/copyCampaign";
import {
  createCampaign,
  createCampaignVariables
} from "src/graphql/generated/createCampaign";
import {
  CampaignFilterInput,
  CampaignStatus
} from "src/graphql/generated/globalTypes";
import {
  listCampaigns,
  listCampaignsVariables
} from "src/graphql/generated/listCampaigns";
import {
  listPinnedCampaigns,
  listPinnedCampaignsVariables
} from "src/graphql/generated/listPinnedCampaigns";
import {
  pauseCampaign,
  pauseCampaignVariables
} from "src/graphql/generated/pauseCampaign";
import {
  setCampaignPinned,
  setCampaignPinnedVariables
} from "src/graphql/generated/setCampaignPinned";
import {
  unarchiveCampaign,
  unarchiveCampaignVariables
} from "src/graphql/generated/unarchiveCampaign";
import {
  unpauseCampaign,
  unpauseCampaignVariables
} from "src/graphql/generated/unpauseCampaign";
import useMutation from "src/util/useMutation";

import CampaignListControls from "./CampaignListControls";
import CampaignTable from "./CampaignTable";
import { CAMPAIGN_SORTS, CAMPAIGN_STATUSES } from "./constants";
import {
  CREATE_CAMPAIGN,
  LIST_CAMPAIGNS,
  LIST_PINNED_CAMPAIGNS,
  ARCHIVE_CAMPAIGN,
  UNARCHIVE_CAMPAIGN,
  PAUSE_CAMPAIGN,
  UNPAUSE_CAMPAIGN,
  COPY_CAMPAIGN,
  SET_CAMPAIGN_PINNED
} from "./queries";

const useStyles = makeStyles(theme => ({
  pinnedCampaignsTable: {
    marginBottom: theme.spacing(8)
  },
  autoArchiveAlert: {
    display: "flex",
    justifyContent: "center"
  }
}));

export default function CampaignList(): JSX.Element {
  const classes = useStyles();
  const { subaccount, subaccountLink } = useSubaccount()!;
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [statusFilter, setStatusFilter] = useQueryParam(
    "status",
    withDefault(ArrayParam, [
      "DRAFT",
      "ACTIVE",
      "PAUSING",
      "PAUSED",
      "STALLED",
      "COMPLETE"
    ])
  );
  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, CAMPAIGN_SORTS[0].key)
  );

  const filter: CampaignFilterInput = {};
  if (search) {
    filter.nameContains = search;
  }
  if (statusFilter && statusFilter.length > 0) {
    filter.statusIn = statusFilter.filter(status =>
      CAMPAIGN_STATUSES.find(s => s.value === status)
    ) as CampaignStatus[];
  }

  const sortValue =
    CAMPAIGN_SORTS.find(availableSort => availableSort.key === sort)?.value ||
    CAMPAIGN_SORTS[0].value;

  const listCampaignsResult = useQuery<listCampaigns, listCampaignsVariables>(
    LIST_CAMPAIGNS,
    {
      variables: {
        limit: pageSize,
        offset: page * pageSize,
        filter,
        sort: sortValue,
        subaccountId: subaccount.id
      },
      pollInterval: 60 * 1000
    }
  );

  const listPinnedCampaignsResult = useQuery<
    listPinnedCampaigns,
    listPinnedCampaignsVariables
  >(LIST_PINNED_CAMPAIGNS, {
    variables: {
      subaccountId: subaccount.id
    },
    pollInterval: 60 * 1000
  });

  const [createCampaign, { loading: createCampaignLoading }] = useMutation<
    createCampaign,
    createCampaignVariables
  >(CREATE_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not create campaign",
    refetchQueries: ["listCampaigns"]
  });

  const [copyCampaign] = useMutation<copyCampaign, copyCampaignVariables>(
    COPY_CAMPAIGN,
    {
      onErrorSnackbarMessage: "Could not copy campaign",
      refetchQueries: ["listCampaigns"]
    }
  );

  const [archiveCampaign] = useMutation<
    archiveCampaign,
    archiveCampaignVariables
  >(ARCHIVE_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not archive campaign",
    refetchQueries: ["listCampaigns"],
    awaitRefetchQueries: true
  });

  const [unarchiveCampaign] = useMutation<
    unarchiveCampaign,
    unarchiveCampaignVariables
  >(UNARCHIVE_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not unarchive campaign",
    refetchQueries: ["listCampaigns"],
    awaitRefetchQueries: true
  });

  const [pauseCampaign] = useMutation<pauseCampaign, pauseCampaignVariables>(
    PAUSE_CAMPAIGN,
    {
      onErrorSnackbarMessage: "Could not pause campaign",
      refetchQueries: ["listCampaigns"],
      awaitRefetchQueries: true
    }
  );

  const [unpauseCampaign] = useMutation<
    unpauseCampaign,
    unpauseCampaignVariables
  >(UNPAUSE_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not unpause campaign",
    refetchQueries: ["listCampaigns"],
    awaitRefetchQueries: true
  });

  const [setCampaignPinned] = useMutation<
    setCampaignPinned,
    setCampaignPinnedVariables
  >(SET_CAMPAIGN_PINNED, {
    onErrorSnackbarMessage: "Could not pin campaign",
    refetchQueries: ["listCampaigns", "listPinnedCampaigns"],
    awaitRefetchQueries: true
  });

  const history = useHistory();

  if (listCampaignsResult.error) {
    return <ErrorCard errors={[listCampaignsResult.error]} />;
  }

  if (listPinnedCampaignsResult.error) {
    return <ErrorCard errors={[listPinnedCampaignsResult.error]} />;
  }

  const goToCampaign = (id: string) =>
    history.push(subaccountLink(`/campaigns/${id}/basics`));

  async function handleNewCampaign() {
    const { data, errors } = await createCampaign({
      variables: {
        name: "New Campaign",
        subaccountId: subaccount.id
      }
    });

    if (!errors) {
      goToCampaign(data!.createCampaign.id);
    }
  }

  async function handleCopyCampaign(campaignId: string) {
    const { data, errors } = await copyCampaign({
      variables: {
        id: campaignId
      }
    });

    if (!errors) {
      goToCampaign(data!.campaignCopy.id);
    }
  }

  async function handleSetPinned(campaignId: string, pinned: boolean) {
    await setCampaignPinned({
      variables: {
        id: campaignId,
        pinned
      }
    });
  }

  function makeCampaignStatusMutationCallback(
    mutation: MutationTuple<any, { id: string }>[0]
  ) {
    return async (campaignId: string) => {
      const { errors } = await mutation({ variables: { id: campaignId } });

      if (errors) {
        // The status mutation failed -- probably someone else changed the status
        // of the campaign out from under us. Re-fetch the campaign list to make
        // sure we have up-to-date data
        await listCampaignsResult.refetch();
        await listPinnedCampaignsResult.refetch();
      }
    };
  }

  const hasPinnedCampaigns =
    !!listPinnedCampaignsResult.data?.listPinnedCampaigns?.length;

  return (
    <Container>
      {hasPinnedCampaigns && (
        <>
          <PageTitle title="Pinned Campaigns" />
          <div className={classes.pinnedCampaignsTable}>
            <CampaignTable
              rows={listPinnedCampaignsResult.data!.listPinnedCampaigns}
              noFooter
              handleArchive={makeCampaignStatusMutationCallback(
                archiveCampaign
              )}
              handleUnarchive={makeCampaignStatusMutationCallback(
                unarchiveCampaign
              )}
              handlePause={makeCampaignStatusMutationCallback(pauseCampaign)}
              handleUnpause={makeCampaignStatusMutationCallback(
                unpauseCampaign
              )}
              handleCopy={handleCopyCampaign}
              handleSetPinned={handleSetPinned}
            />
          </div>
        </>
      )}
      <PageTitle title={hasPinnedCampaigns ? "All Campaigns" : "Campaigns"} />
      <CampaignListControls
        onClickNewCampaign={handleNewCampaign}
        createCampaignLoading={createCampaignLoading}
        statusFilter={statusFilter as string[]}
        setStatusFilter={setStatusFilter}
        search={search}
        setSearch={setSearch}
        sort={sort}
        setSort={setSort}
      />
      {listCampaignsResult.loading || listPinnedCampaignsResult.loading ? (
        <LoadingIndicator />
      ) : (
        <>
          <CampaignTable
            rows={listCampaignsResult.data!.listCampaigns.campaigns}
            handleChangePage={setPage}
            handleChangeRowsPerPage={newRowsPerPage => {
              setPageSize(newRowsPerPage);
              setPage(0);
            }}
            rowsPerPage={pageSize}
            page={page}
            totalCount={listCampaignsResult.data!.listCampaigns.totalCount}
            handleArchive={makeCampaignStatusMutationCallback(archiveCampaign)}
            handleUnarchive={makeCampaignStatusMutationCallback(
              unarchiveCampaign
            )}
            handlePause={makeCampaignStatusMutationCallback(pauseCampaign)}
            handleUnpause={makeCampaignStatusMutationCallback(unpauseCampaign)}
            handleCopy={handleCopyCampaign}
            handleSetPinned={handleSetPinned}
          />
          <div className={classes.autoArchiveAlert}>
            <Alert severity="info">
              Campaigns are automatically archived 30 days after the last call.
              Archived campaigns can be copied.
            </Alert>
          </div>
        </>
      )}
    </Container>
  );
}
